import { SessionListResponse, statesStrToStates, SessionDataResult } from '../interfaces/sessions';
import {
  SessionV2Columns, 
  SESSION_METRICS_COLUMNS, 
  CallMetricColumns,
  PROSPECT_INFO_COLUMNS,
  ReviewStatusColumns,
  SessionAgentsColumns,
  MessagingDataColumns,
  SessionStatesColumns
} from '../interfaces/table';
import {
  ConversationType,
  Counterpart,
  SessionType,
  AgentType,
  Platform,
  OBJECTION_COLUMNS,
  OBJECTION_COLUMNS_TO_HUMAN_READABLE,
  SESSION_METRICS_COLUMNS_TO_HUMAN_READABLE
} from '../interfaces/db';

/**
 * Checks if a session is a call
 */
export const isSessionCall = (session: SessionListResponse): boolean => {
  return session.result.get(SessionV2Columns.CONVERSATION_TYPE) === ConversationType.CALL;
};

/**
 * Checks if a session is an incoming call (creator_agent_id is null)
 */
export const isSessionIncomingCall = (session: SessionListResponse): boolean => {
  return isSessionCall(session) && !session.result.get(SessionAgentsColumns.CREATOR_AGENT_ID);
};

/**
 * Checks if a session is missed (inbound call that wasn't answered)
 */
export const isSessionMissed = (session: SessionListResponse): boolean => {
  return isSessionCall(session) && 
    Boolean(session.result.get(CallMetricColumns.IS_INBOUND)) && 
    !session.result.get(CallMetricColumns.FIRST_USER_JOINED_AT) && 
    session.result.get(SessionV2Columns.SESSION_TYPE) !== SessionType.INTAKE && 
    (!session.result.get(SessionAgentsColumns.AGENT_TYPES_STR) || !session.result.get(SessionAgentsColumns.AGENT_TYPES_STR)?.includes('VOICE_BOT'));
};

/**
 * Checks if a session was answered
 */
export const isSessionAnswered = (session: SessionListResponse): boolean => {
  const liveDuration = session.result.get(SESSION_METRICS_COLUMNS.LIVE_DURATION);
  // Handle both number and string values for live duration
  const numericLiveDuration = typeof liveDuration === 'string' ? parseFloat(liveDuration) : liveDuration;
  return Boolean(numericLiveDuration && numericLiveDuration > 0) || 
    session.result.get(SessionV2Columns.SESSION_TYPE) === SessionType.INTAKE;
};

/**
 * Gets the counterpart type for a session
 */
export const getSessionCounterpart = (session: SessionListResponse): any => {
  return session.result.get(SESSION_METRICS_COLUMNS.BEST_COUNTERPART);
};

/**
 * Checks if the session had prospect answered (not gatekeeper)
 */
export const isSessionProspectAnswered = (session: SessionListResponse): boolean => {
  return getSessionCounterpart(session) === Counterpart.NOT_GATEKEEPER;
};

/**
 * Checks if the session had gatekeeper answered
 */
export const isSessionGatekeeperAnswered = (session: SessionListResponse): boolean => {
  return getSessionCounterpart(session) === Counterpart.GATEKEEPER;
};

/**
 * Gets the user disposition for a session
 */
export const getSessionDisposition = (session: SessionListResponse): any => {
  return session.result.get(SESSION_METRICS_COLUMNS.USER_DISPOSITION);
};

/**
 * Gets the user sentiment for a session
 */
export const getSessionSentiment = (session: SessionListResponse): any => {
  return session.result.get(SESSION_METRICS_COLUMNS.USER_SENTIMENT);
};

/**
 * Gets the correct user ID for a session based on call direction
 * - If incoming call: use first_user_id if set
 * - If outbound call: use creator_agent_id
 * - Otherwise: return undefined
 */
export const getSessionUserId = (session: SessionListResponse): string | undefined => {
  if (isSessionIncomingCall(session)) {
    // For incoming calls, use first_user_id if available
    return session.result.get(SessionAgentsColumns.FIRST_USER_ID) || session.result.get(SessionV2Columns.RESOURCE_ID);
  } else {
    // For outbound calls, use creator_agent_id
    return session.result.get(SessionAgentsColumns.CREATOR_AGENT_ID) || session.result.get(SessionV2Columns.RESOURCE_ID);
  }
};

/**
 * Gets the legacy user ID (resource ID) for backward compatibility
 * This is the old way of getting user ID - use getSessionUserId instead
 * @deprecated Use getSessionUserId instead
 */
export const getSessionResourceId = (session: SessionListResponse): string | undefined => {
  return session.result.get(SessionV2Columns.RESOURCE_ID);
};

/**
 * Gets the objection string for a session (up to 3 objections)
 */
export const getSessionObjectionString = (session: SessionListResponse): string | null => {
  if (!isSessionAnswered(session)) return null;
  
  const objections = OBJECTION_COLUMNS.filter(col => {
    const number_occurences = session.result.get(col);
    return number_occurences && Number(number_occurences) > 0;
  }).map((v) => OBJECTION_COLUMNS_TO_HUMAN_READABLE.get(v) ?? SESSION_METRICS_COLUMNS_TO_HUMAN_READABLE[v]);
  
  return objections.slice(0, 3).join(', ');
};

/**
 * Gets the call duration for a session in seconds
 */
export const getSessionCallDuration = (session: SessionListResponse): number => {
  return session.result.get(SESSION_METRICS_COLUMNS.CALL_DURATION) ?? 0;
};

/**
 * Formats call duration from seconds to MM:SS format
 */
export const formatCallDuration = (durationInSeconds: number): string => {
  if (!durationInSeconds) return '-';
  
  const minutes = Math.floor(durationInSeconds / 60);
  const seconds = Math.round(durationInSeconds % 60);
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

/**
 * Gets the summary text for a session
 * First checks session state for summary fields, then falls back to gen_summary_text
 */
export const getSessionSummary = (session: SessionListResponse): string | undefined => {
  // First try to get summary from session state
  const sessionStates = getSessionState(session);

  // Look for any field with 'summary' in the key name (case insensitive)
  const summaryEntries = Object.entries(sessionStates).filter(([key, value]) =>
    key.toLowerCase().includes('summary') && value && value.length > 0
  );

  // If we found summary fields, use the first non-empty one
  if (summaryEntries.length > 0) {
    return summaryEntries[0][1];
  }

  // Fall back to the original gen_summary_text column
  return session.result.get(SESSION_METRICS_COLUMNS.GEN_SUMMARY_TEXT);
};

/**
 * Gets the rep's phone number for a session
 */
export const getSessionRepNumber = (session: SessionListResponse): string | null => {
  const agentAddress = session.result.get(SessionV2Columns.AGENT_ADDRESS);
  return agentAddress && agentAddress !== '-' ? agentAddress : null;
};

/**
 * Formats a phone number for display
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  
  if (phoneNumber.charAt(0) === '1') {
    return phoneNumber.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
  }
  return phoneNumber.replace(/(\d{1,4})(\d{2,4})(\d{4})/, '+$1 $2 $3');
};

/**
 * Gets the prospect name for a session
 */
export const getSessionProspectName = (session: SessionListResponse): any => {
  return session.result.get(PROSPECT_INFO_COLUMNS.PROSPECT_NAME);
};

/**
 * Gets the company name for a session
 */
export const getSessionCompanyName = (session: SessionListResponse): any => {
  return session.result.get(PROSPECT_INFO_COLUMNS.COMPANY_NAME);
};

/**
 * Gets the prospect title for a session
 */
export const getSessionProspectTitle = (session: SessionListResponse): any => {
  return session.result.get(PROSPECT_INFO_COLUMNS.PROSPECT_TITLE);
};

/**
 * Checks if a session review is open
 */
export const isSessionReviewOpen = (session: SessionListResponse): boolean | null => {
  const value = session.result.get(ReviewStatusColumns.IS_OPEN);
  return value === undefined ? null : Boolean(value);
};

/**
 * Gets the review opening user ID for a session
 */
export const getSessionReviewOpeningUserId = (session: SessionListResponse): string | undefined => {
  return session.result.get(ReviewStatusColumns.OPENING_USER_ID);
};

/**
 * Formats the session start time to a localized time string (HH:MM format)
 */
export const getSessionCallTime = (session: SessionListResponse): string => {
  return session.start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

/**
 * Parses a JSON string of messages, returning an empty array if invalid
 */
export const getSessionMessages = (messageString: any): string[] => {
  if (!messageString) return [];
  try {
    return JSON.parse(messageString as string);
  } catch {
    return [];
  }
};

/**
 * Determines if the last message in a session was from the agent
 */
export const isSessionLastMessageFromAgent = (session: SessionListResponse): boolean => {
  const agentTimesStr = session.result.get(MessagingDataColumns.AGENT_TIMES_STR);
  const customerTimesStr = session.result.get(MessagingDataColumns.CUSTOMER_TIMES_STR);
  
  if (!agentTimesStr && !customerTimesStr) return false;
  if (!customerTimesStr) return true;
  if (!agentTimesStr) return false;
  
  const agentTimes = (agentTimesStr as string).split(',').map(Number);
  const customerTimes = (customerTimesStr as string).split(',').map(Number);
  
  if (!agentTimes.length || !customerTimes.length) return false;
  if (!agentTimes.length) return false;
  if (!customerTimes.length) return true;
  
  const lastAgentTime = agentTimes[agentTimes.length - 1];
  const lastCustomerTime = customerTimes[customerTimes.length - 1];
  
  return lastAgentTime > lastCustomerTime;
};

/**
 * Gets the last message from a session (either agent or customer)
 */
export const getSessionLastMessage = (session: SessionListResponse): string => {
  const agentBodiesStr = getSessionMessages(session.result.get(MessagingDataColumns.AGENT_BODIES_STR));
  const customerBodiesStr = getSessionMessages(session.result.get(MessagingDataColumns.CUSTOMER_BODIES_STR));
  
  if (!agentBodiesStr.length && !customerBodiesStr.length) return 'No messages';
  if (!agentBodiesStr.length) return customerBodiesStr[customerBodiesStr.length - 1];
  if (!customerBodiesStr.length) return agentBodiesStr[agentBodiesStr.length - 1];
  
  const isLastMessageFromAgent = isSessionLastMessageFromAgent(session);
  if (isLastMessageFromAgent) {
    return agentBodiesStr[agentBodiesStr.length - 1];
  }
  return customerBodiesStr[customerBodiesStr.length - 1];
};

/**
 * Gets the session state data as a dictionary of key-value pairs
 * Returns empty object if no session state data is available
 * Now uses STATE_JSON_STR (JSON.parse) instead of STATE_STR (custom parser)
 */
export const getSessionState = (session: SessionListResponse): Record<string, string> => {
  // First try to use state_json_str (preferred method)
  const stateJsonStr = session.result.get(SessionStatesColumns.STATE_JSON_STR);
  if (stateJsonStr && typeof stateJsonStr === 'string') {
    try {
      return JSON.parse(stateJsonStr);
    } catch (e) {
      console.error('Failed to parse state_json_str:', e);
      // Fall through to try state_str
    }
  }

  // Fall back to state_str if state_json_str isn't available or fails
  const stateStr = session.result.get(SessionStatesColumns.STATE_STR);
  if (!stateStr || typeof stateStr !== 'string') {
    return {};
  }

  return statesStrToStates(stateStr);
};

/**
 * Gets the person's name from session state by looking for first_name/name and last_name fields
 * Returns null if no name fields are found
 */
export const getSessionNameFromState = (session: SessionListResponse): string | null => {
  const states = getSessionState(session);

  let firstName = '';
  let lastName = '';

  // First priority: Look for exact matches for first_name and last_name
  if (states['first_name']) {
    firstName = states['first_name'];
  }
  if (states['last_name']) {
    lastName = states['last_name'];
  }

  // Second priority: If no first_name found, look for generic 'name'
  if (!firstName && states['name']) {
    firstName = states['name'];
  }

  // Combine names, trim whitespace
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return fullName || null;
};

/**
 * Gets the person's name from session state data (for SessionDataResult)
 * Returns null if no name fields are found
 */
export const getNameFromSessionStateViaSessionData = (sessionStates: Record<string, string>): string | null => {
  let firstName = '';
  let lastName = '';

  // First priority: Look for exact matches for first_name and last_name
  if (sessionStates['first_name']) {
    firstName = sessionStates['first_name'];
  }
  if (sessionStates['last_name']) {
    lastName = sessionStates['last_name'];
  }

  // Second priority: If no first_name found, look for generic 'name'
  if (!firstName && sessionStates['name']) {
    firstName = sessionStates['name'];
  }

  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || null;
};

/**
 * Gets the prospect name from SessionDataResult, trying prospect_info first, then session state
 * Returns null if no prospect name is found
 */
export const getProspectNameFromSessionData = (data: SessionDataResult): string | null => {
  // First try prospect_info (primary source)
  const prospectInfoName = data.prospect_info?.[PROSPECT_INFO_COLUMNS.PROSPECT_NAME];
  if (prospectInfoName) {
    return prospectInfoName;
  }

  // Fall back to session state if no prospect_info name
  if (data.session_state?.states) {
    const nameFromState = getNameFromSessionStateViaSessionData(data.session_state.states);
    if (nameFromState) {
      return nameFromState;
    }
  }

  return null;
};

/**
 * Checks if a SessionDataResult is from a bot call
 */
export const isSessionDataViaBotCall = (data: SessionDataResult): boolean => {
  return data.session_agents?.agent_types_str?.includes(AgentType.VOICE_BOT) ?? false;
};

/**
 * Checks if a user can edit metrics for a session
 * Returns true if the user is an admin or the session owner
 */
export const canUserEditSessionMetrics = (
  session: SessionListResponse | SessionDataResult,
  currentUserId: string | undefined,
  isAdmin: boolean | null = false
): boolean => {
  if (!currentUserId) return false;
  if (isAdmin === true) return true;

  // Check if this is a SessionListResponse or SessionDataResult
  if ('result' in session) {
    // SessionListResponse
    const sessionUserId = getSessionUserId(session);
    return sessionUserId === currentUserId;
  } else {
    // SessionDataResult - check session.resource_id
    return session.session.resource_id === currentUserId;
  }
};

/**
 * Helper to construct platform link from platform and person ID
 */
const constructPlatformLink = (platform: Platform, personId: string | null): string | null => {
  if (!personId) return null;
  if (platform === Platform.SALESLOFT) {
    return `https://app.salesloft.com/app/people/${personId}`;
  } else if (platform === Platform.OUTREACH) {
    return `https://web.outreach.io/prospects/${personId}/overview`;
  } else if (platform === Platform.APOLLO) {
    return `https://app.apollo.io/#/contacts/${personId}`;
  }
  return null;
};

/**
 * Gets platform URL from session (either from PLATFORM_PERSON_URL or constructed)
 */
export const getPlatformUrlFromSession = (
  session: SessionListResponse,
  sessionDetailsData?: SessionDataResult
): string | null => {
  // Check if sessionDetailsData has prospect_info with platform data
  if (sessionDetailsData?.prospect_info) {
    const prospectInfo = sessionDetailsData.prospect_info;
    const platform = prospectInfo.platform;

    if (platform) {
      const personId = prospectInfo[PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_ID] ?? prospectInfo[PROSPECT_INFO_COLUMNS.XREF_PERSON_ID];
      const platformUrl = prospectInfo[PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_URL] ?? prospectInfo[PROSPECT_INFO_COLUMNS.XREF_PERSON_URL];

      // Priority 1: Use provided URL
      // Priority 2: Construct URL from personId
      return platformUrl || constructPlatformLink(platform, personId || null);
    }
  }

  // Fall back to session.result (TypedResultMap)
  const platform = session.result.get?.(PROSPECT_INFO_COLUMNS.PLATFORM) as Platform | undefined;
  if (!platform) return null;

  const personId = session.result.get?.(PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_ID) ?? session.result.get?.(PROSPECT_INFO_COLUMNS.XREF_PERSON_ID);
  const platformUrl = session.result.get?.(PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_URL) ?? session.result.get?.(PROSPECT_INFO_COLUMNS.XREF_PERSON_URL);

  // Priority 1: Use provided URL
  // Priority 2: Construct URL from personId
  return platformUrl || constructPlatformLink(platform, personId || null);
};

/**
 * Gets platform from session
 */
export const getPlatformFromSession = (
  session: SessionListResponse,
  sessionDetailsData?: SessionDataResult
): Platform | null => {
  // Check if sessionDetailsData has prospect_info with platform data
  if (sessionDetailsData?.prospect_info?.platform) {
    return sessionDetailsData.prospect_info.platform;
  }

  // Fall back to session.result (TypedResultMap)
  return session.result.get?.(PROSPECT_INFO_COLUMNS.PLATFORM) as Platform || null;
};